import axios from 'axios';
import {
  fetchNotificationsStart,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  loadMoreStart,
  loadMoreSuccess,
  loadMoreFailure,
  markGroupRead as markGroupReadAction,
  markAllRead as markAllReadAction,
  setUnreadTaskCount,
  socketNotificationReceived,
  applyPendingNotifications,
  setActiveFilter,
  addReconnectPendingGroups,
} from './notificationsSlice';
import { ACTIVITY_FILTERS, NOTIFICATION_TYPES } from './constants';

const baseUrl = process.env.REACT_APP_API_URL;
const baseUrlV2 = () => baseUrl?.replace('/api/v1', '/api/v2');

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const getWorkspaceId = () =>
  sessionStorage.getItem('workspaceId') || localStorage.getItem('workspaceId');

function buildFilterParams(filter, options = {}) {
  const params = {};
  if (options.cursor) params.cursor = options.cursor;

  switch (filter) {
    case ACTIVITY_FILTERS[1]:
      params.types = `${NOTIFICATION_TYPES.TASK_NOTIFICATION},${NOTIFICATION_TYPES.COMMENT_NOTIFICATION}`;
      break;
    case ACTIVITY_FILTERS[2]:
      params.mentioned = true;
      break;
    case ACTIVITY_FILTERS[3]:
      params.types = NOTIFICATION_TYPES.CHAT_REPLY_NOTIFICATION;
      break;
    default:
      break;
  }
  return params;
}

export const fetchNotifications =
  (filter = ACTIVITY_FILTERS[0]) =>
  async (dispatch) => {
    dispatch(fetchNotificationsStart({ filter }));
    dispatch(setActiveFilter(filter));
    try {
      const { data } = await axios.get(
        `${baseUrlV2()}/notifications/workspace/${getWorkspaceId()}`,
        {
          headers: authHeader(),
          params: buildFilterParams(filter),
        },
      );
      dispatch(
        fetchNotificationsSuccess({
          ...data.notifications,
          serverTime: data.serverTime,
        }),
      );
    } catch (err) {
      dispatch(fetchNotificationsFailure(err.message));
    }
  };

export const fetchMoreNotifications = () => async (dispatch, getState) => {
  const state = getState().notificationReducer;
  if (!state.hasMore || state.loadingMore || !state.nextCursor) return;

  dispatch(loadMoreStart());
  try {
    const { data } = await axios.get(
      `${baseUrlV2()}/notifications/workspace/${getWorkspaceId()}`,
      {
        headers: authHeader(),
        params: buildFilterParams(state.activeFilter, {
          cursor: state.nextCursor,
        }),
      },
    );
    dispatch(
      loadMoreSuccess({
        ...data.notifications,
        serverTime: data.serverTime,
      }),
    );
  } catch (err) {
    dispatch(loadMoreFailure(err.message));
  }
};

export const markGroupRead = (taskId) => async (dispatch) => {
  dispatch(markGroupReadAction({ taskId }));
  try {
    await axios.put(
      `${baseUrlV2}/notifications/read/task/${taskId}`,
      {},
      { headers: authHeader() },
    );
  } catch {
    // optimistic update stays on API failure
  }
};

export const markAllRead = () => async (dispatch) => {
  dispatch(markAllReadAction());
  try {
    await axios.put(`${baseUrl}/notifications/read/all`, {}, { headers: authHeader() });
    dispatch(setUnreadTaskCount(0));
  } catch {
    // optimistic update stays on API failure
  }
};

export const newSocketNotification = (payload) => (dispatch) => {
  const wsId = sessionStorage.getItem('workspaceId') || localStorage.getItem('workspaceId');
  const notifWsId = payload?.notification?.workspaceId;
  if (notifWsId && wsId && notifWsId.toString() !== wsId.toString()) return;
  dispatch(socketNotificationReceived(payload));
};

export const applyPending = () => (dispatch) => {
  dispatch(applyPendingNotifications());
};

export const syncNotificationsAfterReconnect = () => async (dispatch, getState) => {
  try {
    const { loading, lastSeenTimestamp } = getState().notificationReducer || {};
    // Only ever run as a cheap incremental delta. If a notifications fetch is
    // already in flight (e.g. the Layout bootstrap on load) or we have no
    // baseline yet, skip entirely — that fetch already covers us. This avoids a
    // redundant full ~641KB refetch when the socket connects before the
    // bootstrap response has set `lastSeenTimestamp` (common on warm loads).
    if (loading || !lastSeenTimestamp) return;
    const since = lastSeenTimestamp;
    const { data } = await axios.get(
      `${baseUrlV2()}/notifications/workspace/${getWorkspaceId()}`,
      { headers: authHeader(), params: { since } },
    );

    const freshGroups = data?.notifications?.unreadGroups ?? [];
    if (freshGroups.length === 0) return;

    const state = getState().notificationReducer;
    const knownTaskIds = new Set([
      ...state.unreadGroups.map((g) => g.taskId),
      ...state.pendingGroups.map((g) => g.taskId),
    ]);

    const newGroups = freshGroups.filter((g) => !knownTaskIds.has(g.taskId));
    if (newGroups.length > 0) {
      dispatch(addReconnectPendingGroups({ groups: newGroups }));
    }
  } catch {
    // silent
  }
};
